import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, RowData } from '@tanstack/react-table'

import { Chip, ChipProps, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { t as lt } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import colors from '@/assets/scss/_themes-vars.module.scss'

import ChipButton from '@/components/ui/buttons/ChipButton'
import HeaderConfiguration from '@/components/ui/tables/HeaderConfiguration'
import Table, { TableProps } from '@/components/ui/tables/Table'

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    assignLicenses?: (staffId: string, licenses?: string[]) => void
  }
}

export type StaffData = {
  id: string
  name: string
  surname: string
  cps?: string
  email?: string
  status: 'PDS' | 'MEDECIN'
  simplifiedReceptionMode?: boolean
}

export type StaffsTableProps = Pick<TableProps<StaffData>, 'data' | 'loading' | 'onPaginationChange'> & {
  onAssignLicensesClick?: (staffId: string) => void
  onDeleteClick?: (staffId: string) => void
  /** When set, a column lets the admin turn the simplified reception interface on or off. */
  onSimplifiedReceptionModeChange?: (staffId: string, enabled: boolean) => void
  rowsPerPageOptions?: number[]
  totalCount: number
}

const columnHelper = createColumnHelper<StaffData>()

export function StaffsTable(props: StaffsTableProps) {
  const { data, loading, onAssignLicensesClick, onDeleteClick, onPaginationChange, onSimplifiedReceptionModeChange, totalCount } = props
  const { t } = useTranslation()
  useLingui()

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('surname', {
        header: t('page.staffs.staffsTable.header.surname').toString(),
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {getValue().toUpperCase()}
          </Typography>
        ),
      }),
      columnHelper.accessor('name', {
        header: t('page.staffs.staffsTable.header.name').toString(),
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor('email', {
        header: t('page.staffs.staffsTable.header.email').toString(),
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor('cps', {
        header: t('page.staffs.staffsTable.header.cps').toString(),
      }),
      columnHelper.accessor('status', {
        header: t('page.staffs.staffsTable.header.status').toString(),
        cell: ({ getValue }) => {
          const status = getValue()
          let color: ChipProps['color']
          switch (status) {
            case 'PDS':
              color = 'primary'
              break
            case 'MEDECIN':
              color = 'secondary'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={status} size="small" variant="filled" />
        },
      }),
      ...(onSimplifiedReceptionModeChange
        ? [
            columnHelper.accessor('simplifiedReceptionMode', {
              header: lt`Simplified reception`,
              // Reception staff only: a doctor can not be put in the simplified reception (also refused by the API).
              cell: ({ getValue, row }) =>
                row.original.status !== 'PDS' ? null : (
                  <Tooltip title={lt`Only lets this person put patients into waiting rooms`}>
                    <Switch
                      checked={!!getValue()}
                      inputProps={{ 'aria-label': lt`Simplified reception for ${row.original.name} ${row.original.surname}` }}
                      data-testid={`simplified-reception-${row.original.id}`}
                      onChange={(_event, checked) => onSimplifiedReceptionModeChange(row.original.id, checked)}
                    />
                  </Tooltip>
                ),
            }),
          ]
        : []),
      columnHelper.accessor('id', {
        header: t('page.staffs.staffsTable.header.id').toString(),
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {getValue()}
          </Typography>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: HeaderConfiguration,
        cell: ({ row, table }) => (
          <Stack justifyContent="center" direction="row" spacing={1}>
            <Tooltip title={t('table.staffs.actions.assignLicenses')}>
              <ChipButton onClick={() => table.options.meta?.assignLicenses?.(row.original.id)} data-testid={`assign-licenses-${row.original.id}`}>
                <ManageAccountsRoundedIcon />
              </ChipButton>
            </Tooltip>
            <Tooltip title={t('common:button.delete.tooltip')}>
              <ChipButton
                color="error"
                data-testid={`delete-staff-${row.original.id}`}
                onClick={() => {
                  table.options.meta?.delete?.(row.original.id)
                }}
              >
                <DeleteRoundedIcon htmlColor="red" />
              </ChipButton>
            </Tooltip>
          </Stack>
        ),
        meta: {
          align: 'center',
          sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
          sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
        },
      }),
    ]
  }, [t, onSimplifiedReceptionModeChange])

  return (
    <Table
      columns={columns}
      data={data}
      disableGlobalSearch
      loading={loading}
      manualPagination
      meta={{
        assignLicenses: onAssignLicensesClick,
        delete: onDeleteClick,
      }}
      onPaginationChange={onPaginationChange}
      totalRows={totalCount}
    />
  )
}

export default StaffsTable
